import { Layer } from 'shared/map-exports';
import { MapClickGetPois, MapLayerProps } from 'features/mapping/mapping.types';
import { FemaFloodHazardPoi } from 'features/mapping/components/dialogs/PoiDialog/PoiDialog.types';
import { BELOW_STREETS_LAYER_ID } from 'features/mapping/styles';
import {
  getSelectedPatternStyle,
  getSelectedStrokeStyle,
} from 'features/mapping/mapping.utils';
import { usePoisState } from 'state/usePoisState';
import { useMapLayersState } from 'state/useMapLayersState';
import { addVisible } from 'shared/addVisible';
import { VisibilityToggledSource } from '../VisibilityToggledSource';
import { getWatchdutyTileUrl } from '../layers.utils';
import {
  FEMA_FLOOD_HAZARD_SELECTED_FILL_LAYER_ID,
  FEMA_FLOOD_HAZARD_SELECTED_STROKE_LAYER_ID,
  MIN_ZOOM_LEVEL,
  SOURCE_ID,
  SOURCE_LAYER,
} from './FemaFloodHazardLayer.constants';
import {
  getLayerFillStyle,
  getPatternFillStyle,
  getStrokeStyle,
  selectedPatternExpression,
} from './FemaFloodHazardLayer.styles';

type FemaFloodHazardFeatureProperties = {
  riskCategory: FemaFloodHazardPoi['riskCategory'];
  studyRevDate?: string;
  studyEffDate?: string;
};

// String date in YYYY-MM-DD format is expected
const parseDate = (string: string): Date => {
  return new Date(`${string}T00:00:00`);
};

export const femaFloodHazardClickHandler: MapClickGetPois<
  FemaFloodHazardFeatureProperties
> = (geoJsonFeatures) => {
  return geoJsonFeatures.map((feature) => {
    const { riskCategory, studyRevDate, studyEffDate } = feature.properties;
    const metadata: FemaFloodHazardPoi = {
      riskCategory,
      studyRevDate: studyRevDate ? parseDate(studyRevDate) : undefined,
      studyEffDate: studyEffDate ? parseDate(studyEffDate) : undefined,
    };

    return {
      type: 'femaFloodHazard',
      id: feature.id,
      metadata,
    };
  });
};

export const FemaFloodHazardLayer = (props: MapLayerProps): JSX.Element => {
  const { visible } = props;
  const { selectedPoi } = usePoisState();
  const { isDarkBaseLayer } = useMapLayersState();

  const selectedId =
    selectedPoi?.type === 'femaFloodHazard' ? selectedPoi.id || 0 : 0;

  const selectedFillStyle = getSelectedPatternStyle({
    idKey: ['id'],
    selectedId,
    otherLayerProps: {
      'source-layer': SOURCE_LAYER,
      minzoom: MIN_ZOOM_LEVEL,
    },
    patternIconName: selectedPatternExpression,
  });

  const selectedStrokeStyle = getSelectedStrokeStyle({
    idKey: ['id'],
    selectedId,
    otherLayerProps: {
      'source-layer': SOURCE_LAYER,
      minzoom: MIN_ZOOM_LEVEL,
    },
  });

  return (
    <VisibilityToggledSource
      url={getWatchdutyTileUrl('fema_flood_zones')}
      visible={visible}
      id={SOURCE_ID}
    >
      <Layer
        beforeId={BELOW_STREETS_LAYER_ID}
        {...addVisible(getLayerFillStyle(isDarkBaseLayer), visible)}
      />
      <Layer
        beforeId={BELOW_STREETS_LAYER_ID}
        {...addVisible(getStrokeStyle(isDarkBaseLayer), visible)}
      />
      <Layer
        beforeId={BELOW_STREETS_LAYER_ID}
        {...addVisible(getPatternFillStyle(isDarkBaseLayer), visible)}
      />
      <Layer
        id={FEMA_FLOOD_HAZARD_SELECTED_FILL_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...addVisible(selectedFillStyle, visible)}
      />
      <Layer
        id={FEMA_FLOOD_HAZARD_SELECTED_STROKE_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...addVisible(selectedStrokeStyle, visible)}
      />
    </VisibilityToggledSource>
  );
};
